import { useEffect, useState } from 'react';
import { BetDetails } from '../models/MyBetModel.tsx';
import { getBetsByStatus } from '../services/player/BetService.tsx';
import { peekCache, seedCache } from '../helpers/queryCache.tsx';
import { useUser } from '../context/UserContext.tsx';

export const playerMyBetsCacheKey = (userName: string) =>
    `playerPendingBets:${userName}`;

export function usePlayerMyBets() {
    const { user } = useUser();
    const userName = user?.userName ?? '';
    const cacheKey = userName ? playerMyBetsCacheKey(userName) : '';

    const [bets, setBets] = useState<BetDetails[]>(() =>
        cacheKey ? (peekCache<BetDetails[]>(cacheKey) ?? []) : []
    );
    const [isLoading, setIsLoading] = useState(
        () => !cacheKey || peekCache<BetDetails[]>(cacheKey) === undefined
    );

    useEffect(() => {
        if (!userName || !cacheKey) {
            setBets([]);
            setIsLoading(false);
            return;
        }

        const cached = peekCache<BetDetails[]>(cacheKey);
        if (cached) {
            setBets(cached);
            setIsLoading(false);
        }

        let cancelled = false;
        getBetsByStatus(userName).then((nextBets) => {
            if (cancelled) return;
            seedCache(cacheKey, nextBets);
            setBets(nextBets);
            setIsLoading(false);
        }).catch(() => {
            if (!cancelled) setIsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [userName, cacheKey, user?.available]);

    return { bets, isLoading, userName };
}
